import React from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import LinkIcon from '@atlaskit/icon/core/link';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { Expand, ExpandList } from '../../../../../../../common/ui/expand-list';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import {
	getLinksFromText,
	LinkAttachments,
	buttonLabel as linkAttachmentsButtonLabel,
} from '../../../../../../../common/ui/link-attachments';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { removeMarkdown } from '../../../../../../../common/utils/remove-markdown';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphPullRequest_AtlassianSearchPage$key } from './__generated__/searchGraphPullRequest_AtlassianSearchPage.graphql';
import { messages } from './messages';

export const SearchGraphPullRequest = ({
	node,
	position,
	cloudId,
	primaryProduct,
	selectedProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphPullRequest_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphPullRequest_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				url
				permissionLevel
				excerpt
				containerName @optIn(to: "SearchPerformanceImprovementChanges")
				lastModifiedDate
				title
				entity {
					... on ExternalPullRequest {
						title
						lastUpdate
						pullRequestId
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalRepository {
								name
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const {
		url,
		permissionLevel,
		entity,
		excerpt,
		containerName,
		lastModifiedDate,
		title: partialTitle,
	} = data;
	const { title: entityTitle, lastUpdate, container, pullRequestId } = entity || {};
	const label = formatMessage(messages.resultLabel);

	const lastModifiedComponent =
		lastModifiedDate || lastUpdate ? (
			<LastModified lastModified={lastUpdate ?? lastModifiedDate} />
		) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const getDescription = (excerpt?: string | null) => {
		const markdownRemoved = removeMarkdown(excerpt);
		return processExcerpt(markdownRemoved, true);
	};

	const headerMetadataParts = [
		label,
		['pull-request-id', pullRequestId ? `#${pullRequestId}` : null],
		['repository-name', container?.name ?? containerName ?? null],
		['padlock', padlockIcon],
		['last-modified', lastModifiedComponent],
	];

	const links: string[] = getLinksFromText(excerpt || '');
	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const shouldShowRelatedLinks = linkedResults && linkedResults.length > 0;

	const footerComponent = shouldShowRelatedLinks ? (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	) : (
		links.length > 0 && (
			<ExpandList>
				<Expand
					icon={LinkIcon}
					attachmentCount={links.length}
					attachmentType="url"
					label={formatMessage(linkAttachmentsButtonLabel, { count: links.length })}
				>
					<LinkAttachments links={links} onResultClicked={onResultClicked} />
				</Expand>
			</ExpandList>
		)
	);

	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={getDescription(excerpt)} />}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(partialTitle ?? entityTitle)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={data}
			appMode={appMode}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
